import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Cascader, DatePicker, Empty, Radio, Spin, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import StatsKPIGroup, { type KPIItem } from '../../components/Business/StatsKPIGroup';
import DataTable from '../../components/Charts/DataTable';
import { mainBodyStoreUnionMultiSelect } from '../../components/Business/QueryFieldPresets';
import { trafficQualityService } from '../../services/statistics/traffic-quality';
import { useMainBodyMap, useShopMap } from '../../services/store/organization.store';
import type {
  StoreBusinessOverviewRowEntity,
  StoreTrafficOverviewResponseEntity,
  StoreTrafficOverviewRowEntity,
  StoreTrafficRiskSummaryEntity,
} from '../../types/entity/traffic-quality';
import { OrderType } from '../../types/process/order';
import { markShopSortFunc } from '../../utils/format.utils';
import './ShopDataBoard.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type QueryState = {
  dateRange: [Dayjs, Dayjs];
  storePaths: (string | number)[][];
};

type RiskMode = 'order' | 'shipment';
type RiskKey = 'blind' | 'pass' | 'pre' | 'high';
type DeviceGroupKey = 'newApple' | 'secondApple' | 'newAndroid';

const SHIPPED_RRZU_STATUSES = [3, 4, 5, 6, 20];
const EMPTY_RISK_SUMMARY: StoreTrafficRiskSummaryEntity = {
  totalOrders: 0,
  shippedOrders: 0,
  blindCount: 0,
  blindShipped: 0,
  passCount: 0,
  passShipped: 0,
  preCount: 0,
  preShipped: 0,
  highCount: 0,
  highShipped: 0,
};
const EMPTY_RESPONSE: StoreTrafficOverviewResponseEntity = {
  businessRows: [],
  orderRows: [],
  riskSummary: EMPTY_RISK_SUMMARY,
};
const EMPTY_BUSINESS_ROW_BASE: Omit<StoreBusinessOverviewRowEntity, 'storeId'> = {
  incomingOrderCount: 0,
  auditedOrderCount: 0,
  releaseOrderCount: 0,
  shippedOrderCount: 0,
  greenChannelApprovedCount: 0,
  greenChannelShippedCount: 0,
  greenChannelIncomingCount: 0,
  greenChannelApprovalRate: 0,
  greenChannelShipmentRate: 0,
};
const EMPTY_ORDER_ROW_BASE: Omit<StoreTrafficOverviewRowEntity, 'storeId'> = {
  totalOrders: 0,
  shippedOrders: 0,
  returnAnytimeOrders: 0,
  returnAtEndOrders: 0,
  newAppleBlindCount: 0,
  newAppleBlindShipped: 0,
  newApplePassCount: 0,
  newApplePassShipped: 0,
  newApplePreCount: 0,
  newApplePreShipped: 0,
  newAppleHighCount: 0,
  newAppleHighShipped: 0,
  secondAppleBlindCount: 0,
  secondAppleBlindShipped: 0,
  secondApplePassCount: 0,
  secondApplePassShipped: 0,
  secondApplePreCount: 0,
  secondApplePreShipped: 0,
  secondAppleHighCount: 0,
  secondAppleHighShipped: 0,
  newAndroidBlindCount: 0,
  newAndroidBlindShipped: 0,
  newAndroidPassCount: 0,
  newAndroidPassShipped: 0,
  newAndroidPreCount: 0,
  newAndroidPreShipped: 0,
  newAndroidHighCount: 0,
  newAndroidHighShipped: 0,
  blindCount: 0,
  blindShipped: 0,
  passCount: 0,
  passShipped: 0,
  preCount: 0,
  preShipped: 0,
  highCount: 0,
  highShipped: 0,
};

const buildDefaultQueryState = (): QueryState => ({
  dateRange: [dayjs(), dayjs()],
  storePaths: [],
});

const formatPercent = (value: number, precision = 2) => `${Number.isFinite(value) ? value.toFixed(precision) : '0.00'}%`;

const renderJumpValue = (content: React.ReactNode, onClick: () => void, className?: string) => (
  <span
    className={className ? `shop-data-board-link ${className}` : 'shop-data-board-link'}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
  >
    {content}
  </span>
);

const renderRatioJumpValue = (
  numerator: React.ReactNode,
  denominator: React.ReactNode,
  onNumeratorClick: () => void,
  onDenominatorClick: () => void,
  linkClassName?: string,
  wrapperClassName?: string,
) => (
  <span className={wrapperClassName ? `shop-data-board-ratio-cell ${wrapperClassName}` : 'shop-data-board-ratio-cell'}>
    {renderJumpValue(numerator, onNumeratorClick, linkClassName)}
    <span className="shop-data-board-ratio-divider">/</span>
    {renderJumpValue(denominator, onDenominatorClick, linkClassName)}
  </span>
);

const renderRatioWithPercent = (
  numerator: React.ReactNode,
  denominator: React.ReactNode,
  percent: number,
  onNumeratorClick: () => void,
  onDenominatorClick: () => void,
) => (
  <span className="shop-data-board-ratio-percent-cell">
    {renderRatioJumpValue(numerator, denominator, onNumeratorClick, onDenominatorClick)}
    <span className="shop-data-board-ratio-percent-text">{formatPercent(percent || 0)}</span>
  </span>
);

const createEmptyBusinessRow = (storeId: number): StoreBusinessOverviewRowEntity => ({
  storeId,
  ...EMPTY_BUSINESS_ROW_BASE,
});

const createEmptyOrderRow = (storeId: number): StoreTrafficOverviewRowEntity => ({
  storeId,
  ...EMPTY_ORDER_ROW_BASE,
});

const ShopDataBoard: React.FC = () => {
  const { shopList, shopMap } = useShopMap();
  const { mainBodyList } = useMainBodyMap();
  const [filters, setFilters] = useState<QueryState>(() => buildDefaultQueryState());
  const [activeQuery, setActiveQuery] = useState<QueryState>(() => buildDefaultQueryState());
  const [loading, setLoading] = useState(false);
  const [riskMode, setRiskMode] = useState<RiskMode>('order');
  const [data, setData] = useState<StoreTrafficOverviewResponseEntity>(EMPTY_RESPONSE);

  const storeSelectorField = useMemo(
    () => mainBodyStoreUnionMultiSelect(mainBodyList, shopList, 'store_id'),
    [mainBodyList, shopList],
  );

  const resolveScopedStoreIds = (query: QueryState): number[] | null => {
    if (query.storePaths.length === 0) {
      return null;
    }
    const scopedIds = query.storePaths
      .map((path) => Number(path[path.length - 1]))
      .filter((value) => Number.isFinite(value) && value > 0);
    return Array.from(new Set(scopedIds)).sort((a, b) => a - b);
  };

  const buildOverviewRequest = (query: QueryState) => {
    const scopedStoreIds = resolveScopedStoreIds(query);
    const filter: Record<string, any> = {
      orderCreatedAt: {
        operator: 'between',
        value: query.dateRange.map((item) => item.format('YYYY-MM-DD')),
      },
    };
    if (scopedStoreIds !== null) {
      filter.storeId = {
        operator: 'in',
        value: scopedStoreIds.length > 0 ? scopedStoreIds : [-1],
      };
    }
    return { filter };
  };

  const queryKey = useMemo(() => JSON.stringify(buildOverviewRequest(activeQuery)), [activeQuery, shopList]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await trafficQualityService.getStoreTrafficOverview(buildOverviewRequest(activeQuery));
        if (!cancelled) {
          setData(response || EMPTY_RESPONSE);
        }
      } catch (error: any) {
        if (!cancelled) {
          setData(EMPTY_RESPONSE);
          message.error(error?.response?.data?.message || error?.message || '查询失败');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [queryKey]);

  const scopedStoreIds = useMemo(() => resolveScopedStoreIds(activeQuery), [activeQuery, shopList]);
  const visibleStoreIds = useMemo(() => {
    if (scopedStoreIds !== null) {
      return scopedStoreIds;
    }
    return shopList
      .map((shop) => Number(shop.code || shop.id || 0))
      .filter((value) => Number.isFinite(value) && value > 0)
      .sort((a, b) => a - b);
  }, [scopedStoreIds, shopList]);

  const openOrderList = (extraFilter: Record<string, any> = {}) => {
    const filter: Record<string, any> = {
      order_created_at: {
        operator: 'between',
        value: activeQuery.dateRange.map((item) => item.format('YYYY-MM-DD')),
      },
      order_type: {
        operator: 'eq',
        value: OrderType.Normal,
      },
      ...extraFilter,
    };
    if (!filter.store_id && scopedStoreIds !== null) {
      filter.store_id = {
        operator: 'in',
        value: scopedStoreIds.length > 0 ? scopedStoreIds : [-1],
      };
    }

    const search = new URLSearchParams();
    search.set('t', 'tab_all');
    search.set('q', JSON.stringify({ filter }));
    window.open(`/order/list?${search.toString()}`, '_blank');
  };

  const buildStoreScopedFilter = (rowStoreId: number, extraFilter: Record<string, any> = {}) => {
    const filter: Record<string, any> = {
      ...extraFilter,
      store_id: {
        operator: 'in',
        value: [rowStoreId],
      },
    };
    return filter;
  };

  const openRiskOrderList = (riskKey: RiskKey, shippedOnly = false, rowStoreId?: number, deviceGroup?: DeviceGroupKey) => {
    const filter: Record<string, any> = {};
    if (rowStoreId) {
      filter.store_id = {
        operator: 'in',
        value: [rowStoreId],
      };
    }
    if (shippedOnly) {
      filter.rrzu_status = {
        operator: 'in',
        value: SHIPPED_RRZU_STATUSES,
      };
    }

    if (riskKey === 'blind') {
      filter.blind_type = {
        operator: 'ne',
        value: '',
      };
    } else {
      filter.blind_type = {
        operator: 'eq',
        value: '',
      };
      filter.risk_type = {
        operator: 'eq',
        value: riskKey === 'pass' ? 'PASS' : riskKey === 'pre' ? 'PRE_PAY' : 'HIGH_RISK',
      };
    }

    if (deviceGroup === 'newApple') {
      filter.product_brand = { operator: 'eq', value: '苹果' };
      filter.product_quality = { operator: 'eq', value: 'NEW' };
    }
    if (deviceGroup === 'secondApple') {
      filter.product_brand = { operator: 'eq', value: '苹果' };
      filter.product_quality = { operator: 'eq', value: 'SECOND_HAND' };
    }
    if (deviceGroup === 'newAndroid') {
      filter.product_brand = { operator: 'ne', value: '苹果' };
      filter.product_quality = { operator: 'eq', value: 'NEW' };
    }

    openOrderList(filter);
  };

  const openBusinessPolicyOrderList = (policy: 'return_anytime' | 'return_at_end', rowStoreId?: number) => {
    const filter: Record<string, any> = {
      lease_return_policy: {
        operator: 'eq',
        value: policy,
      },
    };
    openOrderList(rowStoreId ? buildStoreScopedFilter(rowStoreId, filter) : filter);
  };

  const sortedBusinessRows = useMemo(() => {
    const rowMap = new Map<number, StoreBusinessOverviewRowEntity>();
    (data.businessRows || []).forEach((row) => rowMap.set(row.storeId, row));
    visibleStoreIds.forEach((storeId) => {
      if (!rowMap.has(storeId)) {
        rowMap.set(storeId, createEmptyBusinessRow(storeId));
      }
    });
    return Array.from(rowMap.values()).sort(markShopSortFunc(shopMap));
  }, [data.businessRows, shopMap, visibleStoreIds]);
  const sortedOrderRows = useMemo(() => {
    const rowMap = new Map<number, StoreTrafficOverviewRowEntity>();
    (data.orderRows || []).forEach((row) => rowMap.set(row.storeId, row));
    visibleStoreIds.forEach((storeId) => {
      if (!rowMap.has(storeId)) {
        rowMap.set(storeId, createEmptyOrderRow(storeId));
      }
    });
    return Array.from(rowMap.values()).sort(markShopSortFunc(shopMap));
  }, [data.orderRows, shopMap, visibleStoreIds]);

  const kpiItems = useMemo<KPIItem[]>(() => {
    const riskSummary = data.riskSummary || EMPTY_RISK_SUMMARY;
    const shipmentRate = riskSummary.totalOrders > 0
      ? (riskSummary.shippedOrders / riskSummary.totalOrders) * 100
      : 0;
    return [
      { title: '总进单量', value: riskSummary.totalOrders, color: '#1677ff' },
      { title: '实际发货率', value: shipmentRate, suffix: '%', precision: 2, color: '#ff4d4f' },
      { title: '盲发订单数', value: riskSummary.blindCount, color: '#722ed1' },
      { title: '通过订单数', value: riskSummary.passCount, color: '#1f2937' },
      { title: '预收订单数', value: riskSummary.preCount, color: '#22c55e' },
      { title: '高风险订单数', value: riskSummary.highCount, color: '#faad14' },
    ];
  }, [data.riskSummary]);

  const riskCards = useMemo(() => {
    const riskSummary = data.riskSummary || EMPTY_RISK_SUMMARY;
    const total = riskMode === 'order' ? riskSummary.totalOrders : riskSummary.shippedOrders;
    return [
      {
        key: 'blind' as RiskKey,
        title: '盲发订单',
        color: '#3b82f6',
        value: riskMode === 'order' ? riskSummary.blindCount : riskSummary.blindShipped,
      },
      {
        key: 'pass' as RiskKey,
        title: '通过订单',
        color: '#22c55e',
        value: riskMode === 'order' ? riskSummary.passCount : riskSummary.passShipped,
      },
      {
        key: 'pre' as RiskKey,
        title: '预收订单',
        color: '#f97316',
        value: riskMode === 'order' ? riskSummary.preCount : riskSummary.preShipped,
      },
      {
        key: 'high' as RiskKey,
        title: '高风险订单',
        color: '#ef4444',
        value: riskMode === 'order' ? riskSummary.highCount : riskSummary.highShipped,
      },
    ].map((item) => ({
      ...item,
      percent: total > 0 ? (item.value / total) * 100 : 0,
    }));
  }, [data.riskSummary, riskMode]);

  const businessColumns: ColumnsType<StoreBusinessOverviewRowEntity> = useMemo(() => [
    {
      title: '店铺',
      dataIndex: 'storeId',
      key: 'storeId',
      width: 72,
      align: 'center',
      render: (storeId: number) => <Text strong>{shopMap.get(storeId)?.name || String(storeId)}</Text>,
    },
    {
      title: '进单数',
      dataIndex: 'incomingOrderCount',
      key: 'incomingOrderCount',
      width: 68,
      align: 'center',
      render: (value: number, record) => renderJumpValue(value || 0, () => openOrderList(buildStoreScopedFilter(record.storeId))),
    },
    {
      title: '已审订单数',
      dataIndex: 'auditedOrderCount',
      key: 'auditedOrderCount',
      width: 72,
      align: 'center',
      render: (value: number, record) => renderJumpValue(value || 0, () => openOrderList(buildStoreScopedFilter(record.storeId, {
        audit_status: {
          operator: 'in',
          value: [3, 4],
        },
      }))),
    },
    {
      title: '已放单数',
      dataIndex: 'releaseOrderCount',
      key: 'releaseOrderCount',
      width: 72,
      align: 'center',
      render: (value: number, record) => renderJumpValue(value || 0, () => openOrderList(buildStoreScopedFilter(record.storeId, {
        decision_status: {
          operator: 'eq',
          value: 3,
        },
      }))),
    },
    {
      title: '已发货订单数',
      dataIndex: 'shippedOrderCount',
      key: 'shippedOrderCount',
      width: 84,
      align: 'center',
      render: (value: number, record) => renderJumpValue(value || 0, () => openOrderList(buildStoreScopedFilter(record.storeId, {
        rrzu_status: {
          operator: 'in',
          value: SHIPPED_RRZU_STATUSES,
        },
      }))),
    },
    {
      title: '绿通订单',
      key: 'greenChannel',
      children: [
        {
          title: '放单数/进单数',
          key: 'greenChannelApproved',
          width: 104,
          align: 'center',
          render: (_value: unknown, record: StoreBusinessOverviewRowEntity) => renderRatioWithPercent(
            record.greenChannelApprovedCount || 0,
            record.greenChannelIncomingCount || 0,
            record.greenChannelApprovalRate || 0,
            () => openOrderList(buildStoreScopedFilter(record.storeId, {
              blind_type: {
                operator: 'eq',
                value: '',
              },
              risk_type: {
                operator: 'eq',
                value: 'PASS',
              },
              decision_status: {
                operator: 'eq',
                value: 3,
              },
            })),
            () => openRiskOrderList('pass', false, record.storeId),
          ),
        },
        {
          title: '发货数/进单数',
          key: 'greenChannelShipped',
          width: 104,
          align: 'center',
          render: (_value: unknown, record: StoreBusinessOverviewRowEntity) => renderRatioWithPercent(
            record.greenChannelShippedCount || 0,
            record.greenChannelIncomingCount || 0,
            record.greenChannelShipmentRate || 0,
            () => openRiskOrderList('pass', true, record.storeId),
            () => openRiskOrderList('pass', false, record.storeId),
          ),
        },
      ],
    },
  ], [openOrderList, openRiskOrderList, shopMap]);

  const renderOrderRatioCell = (
    row: StoreTrafficOverviewRowEntity,
    riskKey: RiskKey,
    deviceGroup: DeviceGroupKey,
  ) => {
    const prefix = deviceGroup;
    const capitalizedRiskKey = riskKey === 'blind' ? 'Blind' : riskKey === 'pass' ? 'Pass' : riskKey === 'pre' ? 'Pre' : 'High';
    const count = Number((row as any)[`${prefix}${capitalizedRiskKey}Count`] || 0);
    const shipped = Number((row as any)[`${prefix}${capitalizedRiskKey}Shipped`] || 0);
    return renderRatioJumpValue(
      shipped,
      count,
      () => openRiskOrderList(riskKey, true, row.storeId, deviceGroup),
      () => openRiskOrderList(riskKey, false, row.storeId, deviceGroup),
      'shop-data-board-link-black',
      'shop-data-board-order-ratio-cell',
    );
  };

  const renderOrderRateCell = (row: StoreTrafficOverviewRowEntity, riskKey: RiskKey, deviceGroup: DeviceGroupKey) => {
    const prefix = deviceGroup;
    const capitalizedRiskKey = riskKey === 'blind' ? 'Blind' : riskKey === 'pass' ? 'Pass' : riskKey === 'pre' ? 'Pre' : 'High';
    const count = Number((row as any)[`${prefix}${capitalizedRiskKey}Count`] || 0);
    const shipped = Number((row as any)[`${prefix}${capitalizedRiskKey}Shipped`] || 0);
    const rate = count > 0 ? (shipped / count) * 100 : 0;
    return <Text>{formatPercent(rate)}</Text>;
  };

  const createDeviceColumns = (title: string, deviceGroup: DeviceGroupKey) => ({
    title,
    children: [
      {
        title: '盲发',
        children: [
          {
            title: '发货数/进单数',
            key: `${deviceGroup}-blind-ratio`,
            width: 70,
            align: 'center' as const,
            render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderOrderRatioCell(row, 'blind', deviceGroup),
          },
          {
            title: '发货率',
            key: `${deviceGroup}-blind-rate`,
            width: 70,
            align: 'center' as const,
            render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderOrderRateCell(row, 'blind', deviceGroup),
          },
        ],
      },
      {
        title: '通过',
        children: [
          {
            title: '发货数/进单数',
            key: `${deviceGroup}-pass-ratio`,
            width: 70,
            align: 'center' as const,
            render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderOrderRatioCell(row, 'pass', deviceGroup),
          },
          {
            title: '发货率',
            key: `${deviceGroup}-pass-rate`,
            width: 70,
            align: 'center' as const,
            render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderOrderRateCell(row, 'pass', deviceGroup),
          },
        ],
      },
      {
        title: '预收',
        children: [
          {
            title: '发货数/进单数',
            key: `${deviceGroup}-pre-ratio`,
            width: 70,
            align: 'center' as const,
            render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderOrderRatioCell(row, 'pre', deviceGroup),
          },
          {
            title: '发货率',
            key: `${deviceGroup}-pre-rate`,
            width: 70,
            align: 'center' as const,
            render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderOrderRateCell(row, 'pre', deviceGroup),
          },
        ],
      },
      {
        title: '高风险',
        children: [
          {
            title: '发货数/进单数',
            key: `${deviceGroup}-high-ratio`,
            width: 70,
            align: 'center' as const,
            render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderOrderRatioCell(row, 'high', deviceGroup),
          },
          {
            title: '发货率',
            key: `${deviceGroup}-high-rate`,
            width: 70,
            align: 'center' as const,
            render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderOrderRateCell(row, 'high', deviceGroup),
          },
        ],
      },
    ],
  });

  const orderColumns: ColumnsType<StoreTrafficOverviewRowEntity> = useMemo(() => [
    {
      title: '店铺名',
      dataIndex: 'storeId',
      key: 'storeId',
      width: 50,
      align: 'center',
      fixed: 'left',
      render: (storeId: number) => <Text strong>{shopMap.get(storeId)?.name || String(storeId)}</Text>,
    },
    {
      title: '进单数',
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      width: 50,
      align: 'center',
      fixed: 'left',
      render: (value: number, row: StoreTrafficOverviewRowEntity) => renderJumpValue(value || 0, () => openOrderList(buildStoreScopedFilter(row.storeId)), 'shop-data-board-link-black'),
    },
    {
      title: '已发货订单数',
      dataIndex: 'shippedOrders',
      key: 'shippedOrders',
      width: 50,
      align: 'center',
      fixed: 'left',
      render: (value: number, row: StoreTrafficOverviewRowEntity) => renderJumpValue(value || 0, () => openOrderList(buildStoreScopedFilter(row.storeId, {
        rrzu_status: {
          operator: 'in',
          value: SHIPPED_RRZU_STATUSES,
        },
      })), 'shop-data-board-link-black'),
    },
    {
      title: '已发货订单率',
      key: 'shippedRate',
      width: 80,
      align: 'center',
      fixed: 'left',
      render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => <Text>{formatPercent(row.totalOrders > 0 ? (row.shippedOrders / row.totalOrders) * 100 : 0)}</Text>,
    },
    {
      title: '订单占比',
      children: [
        {
          title: '随租随还',
          key: 'returnAnytimeOrders',
          width: 80,
          align: 'center',
          render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderJumpValue(
            formatPercent(row.totalOrders > 0 ? (row.returnAnytimeOrders / row.totalOrders) * 100 : 0),
            () => openBusinessPolicyOrderList('return_anytime', row.storeId),
            'shop-data-board-link-black',
          ),
        },
        {
          title: '租完可归还',
          key: 'returnAtEndOrders',
          width: 80,
          align: 'center',
          render: (_value: unknown, row: StoreTrafficOverviewRowEntity) => renderJumpValue(
            formatPercent(row.totalOrders > 0 ? (row.returnAtEndOrders / row.totalOrders) * 100 : 0),
            () => openBusinessPolicyOrderList('return_at_end', row.storeId),
            'shop-data-board-link-black',
          ),
        },
      ],
    },
    createDeviceColumns('全新苹果', 'newApple') as any,
    createDeviceColumns('二手苹果', 'secondApple') as any,
    createDeviceColumns('全新安卓', 'newAndroid') as any,
  ], [openBusinessPolicyOrderList, openOrderList, openRiskOrderList, shopMap]);

  const handleSearch = () => {
    setActiveQuery({
      dateRange: filters.dateRange,
      storePaths: filters.storePaths.map((path) => [...path]),
    });
  };

  const handleReset = () => {
    const next = buildDefaultQueryState();
    setFilters(next);
    setActiveQuery(next);
  };

  return (
    <div className="shop-data-board-page">
      <StatsKPIGroup items={kpiItems} />

      <Card className="shop-data-board-query-card" bordered={false}>
        <div className="shop-data-board-query-row">
          <div className="shop-data-board-query-field">
            <span className="shop-data-board-query-label">接单日期：</span>
            <RangePicker
              allowClear={false}
              value={filters.dateRange}
              onChange={(value) => {
                const start = value?.[0];
                const end = value?.[1];
                if (!start || !end) {
                  return;
                }
                setFilters((prev) => ({ ...prev, dateRange: [start, end] }));
              }}
            />
          </div>

          <div className="shop-data-board-query-field">
            <span className="shop-data-board-query-label">主体/店铺：</span>
            <Cascader
              options={storeSelectorField.getCascaderOptions?.({}) as any}
              {...(storeSelectorField.cascaderProps || {})}
              allowClear
              showSearch
              placeholder="请选择主体/店铺"
              value={filters.storePaths as any}
              onChange={(nextValue) => {
                const linked = storeSelectorField.onValueChange?.(nextValue, { store_id: filters.storePaths });
                const nextPaths = ((linked as Record<string, any> | undefined)?.store_id ?? nextValue ?? []) as (string | number)[][];
                setFilters((prev) => ({ ...prev, storePaths: nextPaths }));
              }}
              style={{ minWidth: 320 }}
            />
          </div>

          <div className="shop-data-board-query-actions">
            <Button onClick={handleReset}>重置</Button>
            <Button type="primary" onClick={handleSearch}>查询</Button>
          </div>
        </div>
      </Card>

      <Spin spinning={loading}>
        <div className="shop-data-board-section-grid">
          <Card title="01-业务数据概览" bordered={false} className="shop-data-board-section-card">
            <DataTable
              columns={businessColumns}
              dataSource={sortedBusinessRows}
              rowKey={(row) => String(row.storeId)}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" /> }}
            />
          </Card>

          <Card
            title="02-风控类型占比"
            bordered={false}
            className="shop-data-board-section-card"
            extra={(
              <Radio.Group value={riskMode} onChange={(event) => setRiskMode(event.target.value)}>
                <Radio.Button value="order">订单数量</Radio.Button>
                <Radio.Button value="shipment">发货数量</Radio.Button>
              </Radio.Group>
            )}
          >
            <div className="shop-data-board-risk-list">
              {riskCards.map((item) => (
                <div key={item.key} className="shop-data-board-risk-item">
                  <div className="shop-data-board-risk-icon" style={{ background: `${item.color}1A`, color: item.color }}>
                    {item.title.slice(0, 1)}
                  </div>
                  <div className="shop-data-board-risk-body">
                    <div className="shop-data-board-risk-header">
                      <span className="shop-data-board-risk-title">{item.title}</span>
                      <div className="shop-data-board-risk-meta">
                        {renderJumpValue(item.value.toLocaleString(), () => openRiskOrderList(item.key, riskMode === 'shipment'))}
                        <span className="shop-data-board-risk-percent">{item.percent.toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="shop-data-board-risk-track">
                      <div
                        className="shop-data-board-risk-fill"
                        style={{ width: `${Math.max(item.percent, 0)}%`, background: item.color }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card title="03-订单数据概览" bordered={false} className="shop-data-board-section-card">
          <DataTable
            columns={orderColumns}
            dataSource={sortedOrderRows}
            rowKey={(row) => String(row.storeId)}
            scroll={{ x: 2800 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" /> }}
          />
        </Card>
      </Spin>
    </div>
  );
};

export default ShopDataBoard;
